/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Shared shell for the legal pages (Privacy Policy, Terms & Conditions).
 * Long-form reading: narrow measure, generous line height, quiet accents.
 *
 * Tailwind's typography plugin isn't loaded in this project's v4 CSS config,
 * so the prose primitives are explicit components rather than `prose` classes.
 */
import { useEffect } from "react";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ── Prose primitives ── */

export function Section({
  id,
  heading,
  children,
}: {
  id?: string;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mb-12">
      <h2 className="font-heading font-bold text-xl md:text-2xl text-white mb-4">
        {heading}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p className="text-steel-light leading-relaxed">{children}</p>;
}

export function Sub({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-heading font-semibold text-base text-white mt-6 mb-2">
      {children}
    </h3>
  );
}

export function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-steel-light leading-relaxed">
          <span className="mt-[0.6rem] w-1.5 h-1.5 bg-electric shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-[3px] border-ember bg-card/60 px-5 py-4">
      <p className="text-steel-light leading-relaxed">{children}</p>
    </div>
  );
}

/* ── Page shell ── */

export default function LegalLayout({
  kicker,
  title,
  lastUpdated,
  summary,
  documentTitle,
  children,
}: {
  kicker: string;
  title: string;
  lastUpdated: string;
  summary: string;
  documentTitle: string;
  children: React.ReactNode;
}) {
  // Client-side navigation needs the title set here; the prerender pass writes
  // the static <head> for direct hits and crawlers.
  useEffect(() => {
    document.title = documentTitle;
    window.scrollTo(0, 0);
    return () => {
      document.title =
        "Blue Collar AI — AI Consulting for Towing, Auto Shops & Blue Collar Businesses";
    };
  }, [documentTitle]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        {/* Header */}
        <header className="relative pt-28 md:pt-36 pb-12 md:pb-16">
          <div className="absolute inset-0 bg-gradient-to-b from-electric/10 to-transparent" />
          <div className="container relative z-10 max-w-3xl">
            <span className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4">
              // {kicker}
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-5">
              {title}
            </h1>
            <p className="text-steel-light text-lg leading-relaxed mb-6">
              {summary}
            </p>
            <p className="font-mono text-xs text-steel tracking-wider uppercase">
              Last updated: {lastUpdated}
            </p>
          </div>
        </header>

        {/* Body */}
        <div className="container max-w-3xl pb-20 md:pb-28">
          <div className="h-px bg-gradient-to-r from-electric/40 to-transparent mb-12" />
          {children}

          <Link
            href="/"
            className="inline-flex items-center gap-2 mt-4 font-heading font-semibold text-sm text-electric hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blue Collar AI
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
